import React, { Suspense, lazy } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from 'react-router-dom';
import { ErrorBoundary, ProtectedRoute, PageLayout } from './components';
import { AuthProvider } from './contexts/AuthContext';
// Eager load - critical landing pages for fast LCP
import { CalculatorPage } from './pages/CalculatorPage';
import ContractorPage from './pages/ContractorPage';
import TaxCodeDetailPage from './pages/TaxCodeDetailPage';
import TaxCodePage from './pages/TaxCodePage';

// Lazy load - non-critical/authenticated pages
const TrackerPage = lazy(() => import('./pages/TrackerPage'));
const HouseholdPage = lazy(() => import('./pages/HouseholdPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'));

// Simple loading fallback for lazy routes
function PageLoadingFallback(): React.JSX.Element {
  return (
    <PageLayout maxWidth="md">
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    </PageLayout>
  );
}

function App(): React.JSX.Element {
  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        // In production, you could send this to an error reporting service
        if (import.meta.env.PROD) {
          console.error('Application error:', error, errorInfo);
          // Example: errorReportingService.captureException(error, { extra: errorInfo });
        }
      }}
    >
      <AuthProvider>
        <Router>
          <Routes>
            <Route
              path="/"
              element={<Navigate to="/calculator" replace />}
            />
            <Route
              path="/calculator"
              element={
                <ErrorBoundary>
                  <CalculatorPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/contractor"
              element={
                <ErrorBoundary>
                  <ContractorPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/tax-code"
              element={
                <ErrorBoundary>
                  <TaxCodePage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/tax-code/:code"
              element={
                <ErrorBoundary>
                  <TaxCodeDetailPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/tracker"
              element={
                <ErrorBoundary>
                  <ProtectedRoute allowAnonymous>
                    <Suspense fallback={<PageLoadingFallback />}>
                      <TrackerPage />
                    </Suspense>
                  </ProtectedRoute>
                </ErrorBoundary>
              }
            />
            <Route
              path="/settings"
              element={
                <ErrorBoundary>
                  <ProtectedRoute>
                    <Suspense fallback={<PageLoadingFallback />}>
                      <HouseholdPage />
                    </Suspense>
                  </ProtectedRoute>
                </ErrorBoundary>
              }
            />
            <Route
              path="/pricing"
              element={
                <ErrorBoundary>
                  <Suspense fallback={<PageLoadingFallback />}>
                    <PricingPage />
                  </Suspense>
                </ErrorBoundary>
              }
            />
            <Route
              path="/login"
              element={
                <ErrorBoundary>
                  <Suspense fallback={<PageLoadingFallback />}>
                    <LoginPage />
                  </Suspense>
                </ErrorBoundary>
              }
            />
            <Route
              path="/auth/callback"
              element={
                <ErrorBoundary>
                  <Suspense fallback={<PageLoadingFallback />}>
                    <AuthCallbackPage />
                  </Suspense>
                </ErrorBoundary>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
